import React, { useState, useRef, useEffect } from 'react';

import GridView from '../components/Grid/GridView';
import FilterModal from '../components/Filters/FilterModal';
import { useActivities } from '../hooks/useActivities';
import { useFilters } from '../hooks/useFilters';
import { useAuth } from '../hooks/useAuth';
import { trackEvent } from '../services/analytics';
import Loading from '../components/Common/Loading';
import { Tag } from '../components/Common/Tag';
import AddressLines from '../components/Common/AddressLines';
import AddActivityDrawer from '../components/Common/AddActivityDrawer';
import AddressHint from '../components/Common/AddressHint';

import { Filters, Location } from '../components/Icons/CustomIcons';
import { CATEGORIES } from '../utils/constants';

const HomePage = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandLocation, setExpandLocation] = useState(false);
  const { filters, updateFilter } = useFilters();

  // Filter öffnen und dabei den Adress-Bereich direkt aufklappen
  const openFilterWithAddress = () => {
    trackEvent('address-dialog-open');
    setExpandLocation(true);
    setFilterOpen(true);
  };

  const handleFilterOpenChange = (next) => {
    setFilterOpen(next);
    if (!next) setExpandLocation(false);
  };
  const { isAuthenticated, user } = useAuth();
  const { activities, total, loading, isFetching, loadingMore, error, loadMore } = useActivities(filters);
  const userLabel = user?.vorname || user?.username || user?.email || 'Benutzer';

  // Datum formatieren (YYYY-MM-DD → «Heute» oder «21.04.2026»)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const today = new Date().toISOString().split('T')[0];
    if (dateStr === today) return 'heute';
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };

  // Drag-to-scroll für Kategorie-Leiste (gleiche Technik wie EventScheduleManager)
  const categoryScrollRef = useRef(null);
  useEffect(() => {
    const el = categoryScrollRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    const onMouseDown = (e) => {
      isDown = true;
      el.classList.add('dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onMouseUp = () => { isDown = false; el.classList.remove('dragging'); };
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('wheel', onWheel);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  if (loading) return <Loading />;
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <div className="activities-page">
        {/* Filter Drawer – alle Bildschirmgrössen */}
        <FilterModal open={filterOpen} onOpenChange={handleFilterOpenChange} expandLocation={expandLocation} />

        <div className="activities-main">
        {/* Aktivitäten-Banner: Headline, Stats, Kategorie-Schnellfilter */}
          <div className="activities-banner">
            {/* Headline & Subline */}
              <h1 className="headings Font-H0-A-120-T">
                {isAuthenticated ? `Hallo, ${userLabel}! Schön, dass du da bist.` : 'Aktivitäten für Ihre Grosskinder finden?'}
              </h1>
              <p className="body Font-H2-A-130-T Colors-Accent-01 activities-banner-subline">
                {isAuthenticated ? 'Finde passende Aktivitäten für deine Grosskinder in deiner Nähe.' : 'Entdecken Sie lokale Angebote, planen Sie Ihre Anreise und verbringen Sie wertvolle Zeit miteinander – alles an einem Ort.'}
              </p>
          </div>
        
        <div className="activities-content">
          {/* Stats & Filter-Button */}
          <div className="activities-banner-stats">
            {/* Anzahl Aktivitäten + Datum/Ort: surrounding box */}
            <div className="activities-banner-info">
              {/* Anzahl Aktivitäten + Datum/Ort */}
              <span className="headings Font-L-A-130-T Colors-Black">
                {total} Aktivitäten {formatDate(filters.departure_date) === 'heute' ? 'heute' : `am ${formatDate(filters.departure_date)}`} in Ihrer Nähe
              </span>
              <button
                type="button"
                className="body Font-S-I-120-T activity-banner-address activity-banner-address-btn"
                onClick={openFilterWithAddress}
                aria-label="Startadresse ändern"
              >
                <Location/>
                <AddressLines address={filters.start_address || 'Adresse eingeben'} />
              </button>
              {/* Hinweis zum Startpunkt – verschwindet automatisch, sobald eine eigene Adresse gesetzt ist */}
              <AddressHint />
            </div>
            {/* Filter-Button (öffnet Modal) */}
            <button
              type="button"
              className="activity-banner-filter-btn"
              onClick={() => setFilterOpen(true)}
              aria-label="Filter öffnen"
            >
              <Filters/>
              <span className="body Font-M-I-120-D Colors-Black activity-banner-filter-label">Filter</span>
            </button>
          </div>
          {/* Kategorie-Schnellfilter: horizontale Scroll-Leiste mit allen Kategorien */}
          <div className="activities-banner-categories-wrapper">
            <div className="activities-banner-categories" ref={categoryScrollRef}>
              <Tag
                label="Alle"
                shape="pill"
                selected={(filters.categories || []).length === 0}
                onClick={() => updateFilter('categories', [])}
              />
              {CATEGORIES.map(cat => (
                <Tag
                  key={cat.id}
                  label={cat.name}
                  shape="pill"
                  selected={(filters.categories || []).includes(cat.name)}
                  onClick={() => {
                    const current = filters.categories || [];
                    const updated = current.includes(cat.name)
                      ? current.filter(c => c !== cat.name)
                      : [...current, cat.name];
                    updateFilter('categories', updated);
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {isFetching && (
            <div style={{ position: 'relative', height: 0, overflow: 'visible' }}>
              <p style={{ position: 'absolute', top: 0, left: 'var(--margin, 1rem)', fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0 }}>
                Aktualisierung...
              </p>
            </div>
          )}
          <GridView activities={activities} total={total} onLoadMore={loadMore} loadingMore={loadingMore} />
        </div>
      </div>

      {/* FAB + Drawer: Aktivität hinzufügen */}
      {isAuthenticated && <AddActivityDrawer />}
    </>
  );
};

export default HomePage;
